import type { PrivacyConsent } from '@/types/share'
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import type {
  SharedVideoDetail,
  CommunityResponse,
  ShareResponse,
  MySharedResponse,
} from '@/types/share'

export function useShareVideo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: { generationId: string } & PrivacyConsent) => {
      return api.post<ShareResponse>('/share', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mySharedVideos'] })
      queryClient.invalidateQueries({ queryKey: ['community'] })
    },
  })
}

export function useUnshareVideo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (shareId: string) => {
      return api.delete<{ success: boolean }>(`/share/${shareId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mySharedVideos'] })
      queryClient.invalidateQueries({ queryKey: ['community'] })
    },
  })
}

export function useMySharedVideos() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const userId = useAuthStore((state) => state.user?.id)

  return useQuery({
    queryKey: ['mySharedVideos', userId],
    queryFn: async () => {
      const data = await api.get<MySharedResponse>('/share/my')
      const map = new Map<string, string>()
      for (const item of data.items) {
        map.set(item.s3Key, item.shareId)
      }
      return map
    },
    enabled: isAuthenticated && !!userId,
    staleTime: 1000 * 60,
  })
}

export function useSharedVideo(shareId: string | null) {
  return useQuery({
    queryKey: ['sharedVideo', shareId],
    queryFn: () => api.get<SharedVideoDetail>(`/share/${shareId}`),
    enabled: !!shareId,
    staleTime: 1000 * 60 * 5,
  })
}

export function useInfiniteCommunity(limit: number = 20) {
  return useInfiniteQuery({
    queryKey: ['community', 'infinite', limit],
    queryFn: ({ pageParam }) =>
      api.get<CommunityResponse>(`/community?page=${pageParam}&limit=${limit}`),
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.page + 1 : undefined,
    initialPageParam: 1,
    staleTime: 1000 * 60,
  })
}

export function useInvalidateShare() {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: ['mySharedVideos'] })
    queryClient.invalidateQueries({ queryKey: ['community'] })
  }
}
